import React, { useEffect, useState } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { useDroppable, useDraggable, DragOverlay } from "@dnd-kit/core";
import { FiEdit2, FiPlus, FiX, FiUser } from "react-icons/fi";
import "./kanbanBoard.css";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import StageTransitionForm from "./StageTransitionForm";
import {
  apiRequest,
  leadsAPI,
  getClientCompane,
  teamAPI,
  getAuthHeader,
} from "../../api/apiHelper";
import {
  addDeal,
  editDeals,
  moveDealToStage,
  addStage,
  getPipelines,
  getDealsForPipeline,
  attachCompaniesToDeal,
} from "../../api/kanbanApi";
import FacebookIcon from "@mui/icons-material/Facebook";
import Toast from "../../components/Toast";
import useToast from "../../hooks/useToast";
import CustomFilterBar, { DATE_OPTIONS } from "../../components/userRing/CustomFilterBar";
import Typography from "@mui/material/Typography";
import "./metaleads/MetaLeadsPage.css";
import { useLocation, useNavigate } from "react-router-dom";

function DraggableLead({
  lead,
  stageId,
  onAssign,
  onConvertToDeal,
  teamMembers,
  userRole,
  onOpenDetails, // NEW
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `lead-${lead.id}`,
    data: { lead, stageId, type: "lead" },
  });

  // Find assigned team member
  const assignedMember = teamMembers?.find(
    (member) => member._id === lead.assignedTo
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  // Extract data from field_data
  const extractFieldValue = (fieldName) => {
    const field = lead.field_data?.find(
      (f) => f.name.toLowerCase() === fieldName.toLowerCase()
    );
    return field?.values?.[0] || null;
  };

  // Get proper name, email, and phone
  const firstName =
    extractFieldValue("first name") || extractFieldValue("first_name");
  const lastName =
    extractFieldValue("last name") || extractFieldValue("last_name");
  const fullName =
    firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || "Unknown Lead";
  const email = extractFieldValue("email") || lead.email || "No email";
  const phone = extractFieldValue("phone") || lead.phone || "No phone";

  return (
    <div
      ref={setNodeRef}
      onClick={() => onOpenDetails && onOpenDetails(lead)} // NEW: open details on click
      style={{
        padding: 16,
        margin: "0 0 12px 0",
        background: isDragging ? "#e8f5e8" : "#fff",
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(30,42,90,0.08)",
        cursor: "pointer",
        opacity: isDragging ? 0.5 : 1,
        fontWeight: 500,
        fontSize: "1.05rem",
        border: "1px solid #e3e6f0",
        borderLeft: "4px solid var(--secondary-color)", // Green border for leads
        transition: "box-shadow 0.2s, background 0.2s",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Drag handle (left side) */}
        <span
          {...attributes}
          {...listeners}
          style={{
            cursor: "grab",
            marginRight: 10,
            fontSize: 18,
            color: "#b0b3c6",
            userSelect: "none",
          }}
          title="Drag"
        >
          &#9776;
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1.08rem",
              color: "#151d3b",
              marginBottom: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Facebook Lead: ${fullName}`}
          >
            {fullName}
          </div>
          <div
            style={{
              color: "#6b7280",
              fontSize: "0.98rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={`Email: ${email}`}
          >
            {email}
          </div>
          {phone !== "No phone" && (
            <div
              style={{
                color: "#6b7280",
                fontSize: "0.9rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={`Phone: ${phone}`}
            >
              {phone}
            </div>
          )}
        </div>

        
        <div style={{ display: "flex", gap: 4 }}>
          {userRole === "manager" && (
            <button
              style={{
                background: "var(--secondary-color)",
                color: "white",
                border: "none",
                borderRadius: 4,
                padding: "4px 8px",
                fontSize: "0.8rem",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onAssign(lead);
              }}
              title="Assign Lead"
            >
              Assign
            </button>
          )}
          <button
            style={{
              background: "var(--primary-color)",
              color: "white",
              border: "none",
              borderRadius: "var(--border-radius)",
              padding: "4px 8px",
              fontSize: "var(--nav-size)",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onConvertToDeal(lead);
            }}
            title="Convert to Deal"
          >
            Convert
          </button>
        </div>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 6 }}
      >
        {/* <span style={{ color: "#151d3b", fontWeight: 500, fontSize: "0.9rem" }}>
          {lead.company !== "No company" ? lead.company : "Facebook Lead"}
        </span> */}
        {assignedMember && (
          <span
            style={{
              color: "#1976d2",
              fontSize: "0.8rem",
              fontWeight: 500,
              background: "#e3f2fd",
              padding: "2px 6px",
              borderRadius: 4,
            }}
            title={`Assigned to: ${assignedMember.name}`}
          >
            👤 {assignedMember.name}
          </span>
        )}
        <span
          style={{
            color: "#1877F2",
            fontSize: "0.8rem",
            fontWeight: "bold",
            marginLeft: "auto",
            marginRight: 8,
          }}
          title="Lead from Facebook"
        >
          <FacebookIcon />
        </span>
        <span style={{ color: "#6b7280", fontSize: "0.8rem" }}>
          {formatDate(lead.created_time)}
        </span>
      </div>
    </div>
  );
}

function DraggableDeal({
  deal,
  stageId,
  onEdit,
  onAssign,
  teamMembers,
  userRole,
  onOpenDetails, // NEW
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: deal.id,
    data: { deal, stageId },
  });

  // Find assigned team member
  const assignedMember = teamMembers?.find(
    (member) => member._id === deal.assignedTo
  );
  return (
    <div
      ref={setNodeRef}
      onClick={() => onOpenDetails?.(deal, stageId)} // NEW: open details on card click
      style={{
        padding: 16,
        margin: "0 0 12px 0",
        background: isDragging ? "#e0e7ff" : "#fff",
        borderRadius: 10,
        boxShadow: "0 2px 8px rgba(30,42,90,0.08)",

        cursor: "pointer",
        opacity: isDragging ? 0.5 : 1,
        fontWeight: 500,
        fontSize: "1.05rem",
        border: "1px solid #e3e6f0",
        transition: "box-shadow 0.2s, background 0.2s",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Drag handle (left side) */}
        <span
          {...attributes}
          {...listeners}
          style={{
            cursor: "grab",
            marginRight: 10,
            fontSize: 18,
            color: "#b0b3c6",
            userSelect: "none",
          }}
          title="Drag"
        >
          &#9776;
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1.08rem",
              color: "#151d3b",
              marginBottom: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <span
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/home/lead/${deal._id || deal.id}`, { state: { deal, fromPage: 'pipeline' } });
              }}
              style={{ cursor: "pointer", color: "#396afc" }}
              title="View deal details"
            >
              {deal.content}
            </span>
          </div>
          {deal.description && (
            <div
              style={{
                color: "#6b7280",
                fontSize: "0.98rem",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {deal.description}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {/* {userRole === "manager" && (
            // <button
            //   style={{
            //     background: "#4caf50",
            //     color: "white",
            //     border: "none",
            //     borderRadius: 4,
            //     padding: "4px 8px",
            //     fontSize: "0.8rem",
            //     cursor: "pointer",
            //   }}
            //   onClick={(e) => {
            //     e.stopPropagation();
            //     onAssign(deal);
            //   }}
            //   title="Assign Deal"
            // >
            //   Assign
            // </button>
          )} */}
          <FiEdit2
            style={{
              color: "var(--primary-color)",
              cursor: "pointer",
              fontSize: 18,
              marginLeft: 8,
            }}
            title="Edit deal"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(deal, stageId);
            }}
          />
        </div>
      </div>
      <div
        style={{ display: "flex", alignItems: "center", marginTop: 10, gap: 6, minWidth: 0, overflow: "hidden" }}
      >
        <span
          style={{
            color: "#151d3b", fontWeight: 600, fontSize: "0.92rem",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            maxWidth: 90, flexShrink: 0,
          }}
          title={`${deal.code_currency === "INR" ? "₹" : deal.code_currency === "USD" ? "$" : deal.code_currency === "EUR" ? "€" : ""}${deal.value ? Number(deal.value).toLocaleString() : ""}`}
        >
          {deal.code_currency === "INR"
            ? "₹"
            : deal.code_currency === "USD"
            ? "$"
            : deal.code_currency === "EUR"
            ? "€"
            : ""}
          {deal.value ? Number(deal.value).toLocaleString() : ""}
        </span>
        {/* Show deal status badge */}
        <span
          className="status-badge"
          style={{
            padding: "2px 8px",
            borderRadius: 6,
            background:
              deal.status === "closed_won"
                ? "#4caf50"
                : deal.status === "closed_lost"
                ? "#f44336"
                : "#7190f3ff",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.8rem",
            letterSpacing: "0.5px",
            flexShrink: 0,
          }}
          title={`Status: ${deal.status || "open"}`}
        >
          {deal.status === "closed_won"
            ? "Won"
            : deal.status === "closed_lost"
            ? "Lost"
            : "Open"}
        </span>
        {assignedMember && (
          <span
            style={{
              color: "#1976d2",
              fontSize: "0.78rem",
              fontWeight: 500,
              background: "#e3f2fd",
              padding: "2px 6px",
              borderRadius: 4,
              marginLeft: "auto",
              cursor: "help",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 80,
              flexShrink: 1,
            }}
            title={`Assigned to: ${assignedMember.name} (${assignedMember.email}) - ${assignedMember.role}`}
          >
            👤 {assignedMember.name}
          </span>
        )}
      </div>
    </div>
  );
}

function DroppableStage({
  stage,
  children,
  onEditStage,
  editingStageId,
  editingStageName,
  setEditingStageName,
  setEditingStageId,
  handleStageNameEdit,
  onAssignLead,
  onConvertToDeal,
  leads,
  teamMembers,
  userRole,
  onOpenLeadDetails, // NEW
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  return (
    <div
      ref={setNodeRef}
      className="droppable_stage"
      style={{ background: isOver ? "#e3e6f0" : "#f4f5fa", height: "100%" }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        {editingStageId === stage.id ? (
          <form
            onSubmit={handleStageNameEdit}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
            }}
          >
            <input
              value={editingStageName}
              onChange={(e) => setEditingStageName(e.target.value)}
              autoFocus
              style={{
                fontSize: "1.1rem",
                fontWeight: 700,
                border: "1.5px solid #e3e6f0",
                borderRadius: 8,
                padding: "2px 8px",
                outline: "none",
                color: "#151d3b",
                background: "#f4f5fa",
                marginRight: 8,
                width: "100%",
              }}
              onBlur={handleStageNameEdit}
              maxLength={32}
            />
            <button type="submit" style={{ display: "none" }} />
          </form>
        ) : (
          <h3
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "#151d3b",
              display: "flex",
              alignItems: "center",
              gap: 6,
              width: "100%",
            }}
          >
            {stage.title}
            <FiEdit2
              style={{
                cursor: "pointer",
                color: "var(--primary-color)",
                marginLeft: 8,
              }}
              size={16}
              onClick={() => onEditStage(stage.id, stage.title)}
              title="Edit stage name"
            />
          </h3>
        )}
      </div>
      {/* Render leads first (only in stage with order 1) and deals inside a scrollable container */}
      <div className="stage-content">
        {stage.order === 1 && leads && leads.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {leads.map((lead) => (
              <DraggableLead
                key={`lead-${lead.id}`}
                lead={lead}
                stageId={stage.id}
                onAssign={onAssignLead}
                onConvertToDeal={onConvertToDeal}
                teamMembers={teamMembers}
                userRole={userRole}
                onOpenDetails={onOpenLeadDetails} // NEW
              />
            ))}
          </div>
        )}

        {/* Render deals */}
        {children}
      </div>
    </div>
  );
}

function DealModal({
  open,
  onClose,
  onSubmit,
  stages,
  initialData,
  isEdit,
  companies = [],
  companiesLoading = false,
  companiesError = null,
  onUpdateCompany,
  savingCompanyId,
  onCreateCompany,
  onAssignDeal,   // NEW: assign handler
  teamMembers = [], // NEW
  userRole,       // NEW
}) {
  const [form, setForm] = useState(
    () =>
      initialData || {
        title: "",
        description: "",
        value: "",
        stageId: stages[0]?.id || "",
        code_currency: "INR",
        endDate: "",
        addStageName: "",
        // (ADDED) companyId field
        companyId: "",
      }
  );
  const [companyForm, setCompanyForm] = useState({
    name: "",
    website: "",
    address: "",
    contactNumbers: [],
  });
  const [createMode, setCreateMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  React.useEffect(() => {
    if (!open) return;

    // RESET create mode whenever dialog opens
    setCreateMode(false);

    if (initialData) {
      setForm({
        ...initialData,
        value: initialData.value !== undefined ? initialData.value : "",
        title: initialData.title || initialData.content || "",
        companyId: initialData.companyId || "",
      });
    } else {
      setForm({
        title: "",
        description: "",
        value: "",
        stageId: stages[0]?.id || "",
        code_currency: "INR",
        endDate: "",
        companyId: "",
      });
    }
  }, [open, initialData, stages]);

  React.useEffect(() => {
    if (createMode) {
      setCompanyForm({
        name: "",
        website: "",
        address: "",
        contactNumbers: [],
      });
      return;
    }
    const sel = companies.find((c) => c.id === form.companyId);
    if (sel) {
      setCompanyForm({
        name: sel.name || "",
        address: sel.address || sel.original?.address || "",
        website: sel.website || sel.original?.website || "",
        contactNumbers: Array.isArray(sel.contactNumbers)
          ? sel.contactNumbers.map((x) => ({
              _id: x._id,
              name: x.name || "",
              number: x.number || "",
              email: x.email || "",
            }))
          : [],
      });
    } else {
      setCompanyForm({
        name: "",
        website: "",
        address: "",
        contactNumbers: [],
      });
    }
  }, [form.companyId, companies, createMode]);
  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }
  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title || !form.value || !form.stageId) return;
    
    setIsSubmitting(true);
    try {
      let finalCompanyId = form.companyId;
      
      // If creating new company, create it first
      if (createMode && onCreateCompany) {
        const created = await onCreateCompany(companyForm);
        if (created?.id) {
          finalCompanyId = created.id;
        }
      }
      
      // Then submit the deal
      onSubmit({
        ...form,
        value: Number(form.value),
        id: form.id || `deal-${Date.now()}`,
        content: form.title,
        companyId: finalCompanyId,
      });
      onClose();
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setIsSubmitting(false);
    }
  }
  const handleCompanySelect = (e) => {
    // NEW
    const value = e.target.value;
    if (value === "__new__") {
      setCreateMode(true);
      setForm((p) => ({ ...p, companyId: "" }));
      setCompanyForm({ name: "", email: "", address: "", contactNumbers: [] });
    } else {
      setCreateMode(false);
      setForm((prev) => ({ ...prev, companyId: value }));
    }
  };

  // NEW: handlers for editable company form
  const handleCompanyField = (e) => {
    const { name, value } = e.target;
    setCompanyForm((p) => ({ ...p, [name]: value }));
  };
  const handleContactChange = (idx, field, value) => {
    setCompanyForm((p) => {
      const list = [...p.contactNumbers];
      list[idx] = { ...list[idx], [field]: value };
      return { ...p, contactNumbers: list };
    });
  };
  const addContact = () =>
    setCompanyForm((p) => ({
      ...p,
      contactNumbers: [
        ...p.contactNumbers,
        { name: "", number: "", email: "" },
      ],
    }));
  const removeContact = (idx) =>
    setCompanyForm((p) => ({
      ...p,
      contactNumbers: p.contactNumbers.filter((_, i) => i !== idx),
    }));

  const savingThisCompany =
    savingCompanyId === form.companyId ||
    (createMode && savingCompanyId === "new");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: "16px", overflow: "hidden" },
      }}
    >
      {/* Polished Header */}
      <Box sx={{
        px: 3, py: 2.5,
        borderBottom: "1px solid #f0f0f0",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "linear-gradient(135deg, #f8f9ff 0%, #fff 100%)",
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#151d3b", lineHeight: 1.2 }}>
            {isEdit ? "Edit Deal" : "Add New Deal"}
          </Typography>
          <Typography variant="caption" sx={{ color: "#6b7280", fontSize: "0.78rem" }}>
            {isEdit ? "Update deal information" : "Fill in the details to create a deal"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#6b7280", "&:hover": { background: "#f3f4f6" } }}>
          <FiX />
        </IconButton>
      </Box>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 0, px: 3, py: 2.5, overflowY: "auto", maxHeight: "65vh" }}>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="company-select-label">Company</InputLabel>
            <Select
              labelId="company-select-label"
              id="company-select"
              name="companyId"
              value={createMode ? "__new__" : form.companyId}
              onChange={handleCompanySelect}
              label="Company"
            >
              <MenuItem value="__new__">+ Create new company</MenuItem>{" "}
              {/* NEW */}
              {!companiesLoading &&
                !companiesError &&
                companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Title"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            InputLabelProps={{ required: false }}
            fullWidth
            sx={{ mb: 2 }}
          />

          {/* Combined Currency (first) + Value (second) in one box */}
          <Box
            sx={{
              mb: 2,
              width: "100%",
              height: 56,
              flexShrink: 0,
              display: "flex",
              alignItems: "stretch",
              border: "1px solid #e3e6f0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Select
              name="code_currency"
              value={form.code_currency}
              onChange={handleChange}
              variant="outlined"
              sx={{
                minWidth: 100,
                "& fieldset": { border: "none" },
                borderRadius: 0,
                height: "100%",
                "& .MuiInputBase-root": { height: "100%" },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 0,
                  px: 2,
                },
              }}
            >
              <MenuItem value="INR">INR</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
            </Select>
            <TextField
              placeholder="Value"
              name="value"
              type="number"
              value={form.value}
              onChange={handleChange}
              variant="outlined"
              sx={{
                flex: 1,
                borderLeft: "1px solid #e3e6f0",
                "& fieldset": { border: "none" },
                "& .MuiInputBase-root": { height: "100%" },
                "& .MuiInputBase-input": { py: 0, px: 2 },
              }}
            />
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="stage-select-label" required={false}>
              Stage
            </InputLabel>
            <Select
              labelId="stage-select-label"
              label="Stage"
              name="stageId"
              value={form.stageId}
              onChange={handleChange}
            >
              {stages.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              label="Status"
              name="status"
              value={form.status || "open"}
              onChange={handleChange}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="closed_won">Closed Won</MenuItem>
              <MenuItem value="closed_lost">Closed Lost</MenuItem>
            </Select>
          </FormControl>
          {/* (ADDED) Company Select  */}

          {/* Company Details Preview */}

          {(createMode || form.companyId) &&
            (() => {
              return (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e3e6f0",
                    borderRadius: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    {createMode ? "Create new company" : "Company details"}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField
                      label="Company Name"
                      name="name"
                      value={companyForm.name}
                      onChange={handleCompanyField}
                      fullWidth
                    />
                    {/* <TextField label="Email" name="email" value={companyForm.email} onChange={handleCompanyField} fullWidth /> */}
                    <TextField
                      label="Website"
                      name="website"
                      value={companyForm.website}
                      onChange={handleCompanyField}
                      fullWidth
                      placeholder=""
                    />
                    <TextField
                      label="Address"
                      name="address"
                      value={companyForm.address}
                      onChange={handleCompanyField}
                      fullWidth
                    />
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ mb: 1 }}
                    >
                      Contacts
                    </Typography>
                    {companyForm.contactNumbers.map((c, idx) => (
                      <Box
                        key={c._id || idx}
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          placeholder="Name"
                          value={c.name}
                          onChange={(e) =>
                            handleContactChange(idx, "name", e.target.value)
                          }
                          sx={{ flex: 1, minWidth: 160 }}
                        />
                        <TextField
                          placeholder="Number"
                          value={c.number}
                          onChange={(e) =>
                            handleContactChange(idx, "number", e.target.value)
                          }
                          sx={{ flex: 1, minWidth: 160 }}
                        />
                        <TextField
                          placeholder="Email"
                          value={c.email || ""}
                          onChange={(e) =>
                            handleContactChange(idx, "email", e.target.value)
                          }
                          sx={{ flex: 1, minWidth: 200 }}
                        />
                        <Button
                          type="button"
                          variant="outlined"
                          color="error"
                          onClick={() => removeContact(idx)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                    <Button
                      type="button"
                      variant="text"
                      onClick={addContact}
                      sx={{ mt: 0.5 }}
                    >
                      + Add contact
                    </Button>
                  </Box>

                  {/* Company will be saved when deal is submitted */}
                  {!createMode && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                        gap: 1,
                      }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        disabled={
                          !onUpdateCompany ||
                          savingThisCompany
                        }
                        onClick={async () => {
                          try {
                            if (!form.companyId) return;
                            await onUpdateCompany?.(
                              form.companyId,
                              companyForm
                            );
                          } catch {}
                        }}
                        sx={{
                          background: "var(--primary-color)",
                          ":hover": { background: "#303f9f" },
                        }}
                      >
                        {savingThisCompany ? "Saving..." : "Save Company"}
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}

          {/* Currency select moved inside combined box above */}
          {/* <TextField
            label="End Date"
            name="endDate"
            type="date"
            value={form.endDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          /> */}
          <TextField
            label="Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            multiline
            minRows={2}
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", gap: 1, justifyContent: "space-between" }}>
          {/* Assign button — only in edit mode when deal exists */}
          <Box>
            {onAssignDeal && initialData?._id && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => { onAssignDeal(initialData); onClose(); }}
                sx={{
                  borderColor: "#e3e6f0",
                  color: "#374151",
                  fontWeight: 600,
                  "&:hover": { borderColor: "var(--primary-color)", color: "var(--primary-color)", background: "#f0f4ff" },
                }}
              >
                👤 Assign Deal
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={onClose} color="inherit" variant="outlined" sx={{ borderColor: "#e3e6f0", color: "#6b7280" }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                background: "var(--primary-color)",
                ":hover": { background: "#303f9f" },
                fontWeight: 600,
                px: 3,
              }}
            >
              {isSubmitting ? "Saving..." : isEdit ? "Save Changes" : "Add Deal"}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function KanbanBoard({ member, selectedStageId, dateFilterParams = {}, showAddStage = true,
  
  // optional kanban-specific filter controls (kept optional for other pages)
  kanbanDateRange = "",
  setKanbanDateRange = null,
  kanbanCustomStart = "",
  setKanbanCustomStart = null,
  kanbanCustomEnd = "",
  setKanbanCustomEnd = null,
}) {
  const { toast, success, error, hideToast } = useToast();
  const [pipeline, setPipeline] = useState(null); // The selected pipeline object
  const [stages, setStages] = useState([]); // Stages with deals
  const [pipelineName, setPipelineName] = useState("");
  const [activeDeal, setActiveDeal] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [editingStageId, setEditingStageId] = useState(null);
  const [editingStageName, setEditingStageName] = useState("");
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [editDealStageId, setEditDealStageId] = useState(null);
  const [addStageName, setAddStageName] = useState("");
  const [addStageTargetType, setAddStageTargetType] = useState("single"); // "single", "group", "members"
  const [addStageMemberIds, setAddStageMemberIds] = useState([]);
  const location = useLocation();
  const paramMember = member || location.state?.member;
  const userId = paramMember?.id || paramMember?._id;

  console.log("selectedStageId", selectedStageId);

  // Leads and team management state
  const [leads, setLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [assigningLead, setAssigningLead] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [showDealAssignModal, setShowDealAssignModal] = useState(false);
  const [dealToAssign, setDealToAssign] = useState(null);
  const [assigningDeal, setAssigningDeal] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);
  const [savingCompanyId, setSavingCompanyId] = useState(null);
  // NEW: deal details drawer state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsDeal, setDetailsDeal] = useState(null);
  const [detailsStageId, setDetailsStageId] = useState(null);
  const [detailsEdit, setDetailsEdit] = useState(false);
  const [detailsForm, setDetailsForm] = useState({
    title: "",
    description: "",
    value: "",
    code_currency: "INR",
    stageId: "",
    companyId: "",
  });
  const [leadDetailsOpen, setLeadDetailsOpen] = useState(false); // NEW
  const [leadDetails, setLeadDetails] = useState(null); // NEW
  // State  for stage model
  const [showStageForm, setShowStageForm] = useState(false);
  const [stageFormLead, setStageFormLead] = useState(null);
  const [stageTargetId, setStageTargetId] = useState(null);

  // Helper for full date-time formatting (matches MetaLeadsPage)
  const formatLeadDateTime = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // Format object keys into friendly labels, e.g. firstName -> First Name
  const formatLabel = (key) =>
    String(key)
      .replace(/([A-Z])/g, " $1")
      .replace(/[_\-]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim();

  // NEW: open/close lead details
  const openLeadDetails = (lead) => {
    setLeadDetails(lead);
    setLeadDetailsOpen(true);
  };
  const closeLeadDetails = () => {
    setLeadDetailsOpen(false);
    setLeadDetails(null);
  };

  function openDealDetails(deal, sId) {
    setDetailsDeal(deal);
    setDetailsStageId(sId);
    setDetailsOpen(true);
  }

  function closeDealDetails() {
    setDetailsOpen(false);
    setDetailsDeal(null);
    setDetailsStageId(null);
    setDetailsEdit(false);
  }

  useEffect(() => {
    if (!detailsOpen || !detailsDeal) return;
    setDetailsEdit(false);
    setDetailsForm({
      title: detailsDeal.title || detailsDeal.content || "",
      description: detailsDeal.description || "",
      value: detailsDeal.value ?? "",
      code_currency: detailsDeal.code_currency || "INR",
      stageId: detailsStageId || detailsDeal.stageId || stages[0]?.id || "",
      companyId:
        (Array.isArray(detailsDeal.clientCompanyIds) &&
          detailsDeal.clientCompanyIds[0]) ||
        detailsDeal.companyId ||
        "",
    });
  }, [detailsOpen, detailsDeal, detailsStageId, stages]);
  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setDetailsForm((p) => ({ ...p, [name]: value }));
  };

  // (ADDED) Fetch companies from backend (no token required per your note)
  const createCompany = async (data) => {
    setSavingCompanyId("new");
    try {
      const payload = {
        companyName: (data.name || "").trim(),
        address: data.address || "",
        website: data.website || "",
        contactNumbers: Array.isArray(data.contactNumbers)
          ? data.contactNumbers
              .map((x) => ({
                name: (x.name || "").trim(),
                number: (x.number || "").trim(),
                email: (x.email || "").trim(),
              }))
              .filter((x) => x.name || x.number || x.email)
          : [],
      };
      const res = await getClientCompane.createCompany(payload);
      const created = res?.data || res;
      if (!created || !created._id)
        throw new Error(res?.message || "Invalid create response");

      const normalized = {
        id: created._id,
        name: String(created.companyName || "Unnamed").trim(),
        email: created.email || "",
        contactNumbers: Array.isArray(created.contactNumbers)
          ? created.contactNumbers
          : [],
        address:
          typeof created.address === "string" ? created.address.trim() : "",
        original: created,
      };

      setCompanies((prev) => [normalized, ...prev]);
      success("Company created");
      return normalized;
    } catch (e) {
      console.error("Create company failed", e);
      error(
        e?.response?.data?.message || e?.message || "Failed to create company",
        { title: "Company" }
      );
      throw e;
    } finally {
      setSavingCompanyId(null);
    }
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    setCompaniesError(null);
    try {
      const res = await getClientCompane.getClientCompanies();
      const list = res?.data.companies || [];
      const normalized = list.map((c) => ({
        id: c._id,
        name: String(c.companyName || "Unnamed").trim(),
        email: c.email || "",
        contactNumbers: Array.isArray(c.contactNumbers) ? c.contactNumbers : [],
        address: typeof c.address === "string" ? c.address.trim() : "",
        original: c, // raw object
      }));
      setCompanies(normalized);
    } catch (err) {
      console.error("Failed to fetch companies", err);
      setCompaniesError("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };
  // NEW: Update company details via backend
  const updateCompanyDetails = async (companyId, data) => {
    setSavingCompanyId(companyId);
    try {
      const payload = {
        companyName: (data.name || "").trim(),
        email: data.email || "",
        address: data.address || "",
        contactNumbers: Array.isArray(data.contactNumbers)
          ? data.contactNumbers
              .map((x) => ({
                _id: x._id, // preserve if present
                name: (x.name || "").trim(),
                number: (x.number || "").trim(),
                email: (x.email || "").trim(),
              }))
              .filter((x) => x.name || x.number || x.email)
          : [],
      };
      const res = await getClientCompane.updateCompany(companyId, payload);
      const updated = res?.data || res?.company || res; // controller returns updatedCompany
      if (!updated || !updated._id) {
        throw new Error(res?.message || "Invalid update response");
      }
      const normalized = {
        id: updated._id,
        name: String(updated.companyName || "Unnamed").trim(),
        email: updated.email || "",
        contactNumbers: Array.isArray(updated.contactNumbers)
          ? updated.contactNumbers
          : [],
        address:
          typeof updated.address === "string" ? updated.address.trim() : "",
        original: updated,
      };

      setCompanies((prev) =>
        prev.map((c) => (c.id === companyId ? normalized : c))
      );
      success("Company updated");
      return normalized;
    } catch (e) {
      console.error("Update company failed", e);
      error(
        e?.response?.data?.message || e?.message || "Failed to update company",
        {
          title: "Company",
        }
      );
      throw e;
    } finally {
      setSavingCompanyId(null);
    }
  };
  // Fetch leads
  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      let response;
      // determine requester role synchronously from localStorage
      let logged = {};
      try { logged = JSON.parse(localStorage.getItem("userData") || "{}"); } catch (e) { logged = {}; }
      const loggedRole = (logged.role || "").toString().toLowerCase();

      if (loggedRole === "manager" && userId) {
        response = await leadsAPI.getLeadsByUser(userId, dateFilterParams);
      } else {
        // members or manager without explicit target -> rely on token
        response = await leadsAPI.getLeadsByUser(undefined, dateFilterParams);
      }
      if (response && response.data) {
        // leadsAPI.getLeadsByUser returns { leads: [...] } or similar
        setLeads(response.data.leads || response.data || []);
      }
    } catch (error) {
      console.error("Failed to load leads:", error);
    } finally {
      setLoadingLeads(false);
    }
  };

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getTeamMembers();
      if (response && response.data && response.data.invites) {
        const registeredMembers = response.data.invites.filter(
          (invite) => invite.status === "complete" && invite.role && invite._id
        );
        setTeamMembers(registeredMembers);
      }
    } catch (error) {
      console.error("Failed to load team members:", error);
    }
  };

  // Get user role from localStorage
  const getUserRole = () => {
    try {
      const userData = localStorage.getItem("userData");
      if (userData) {
        const parsedData = JSON.parse(userData);
        setUserRole(parsedData.role);
      }
    } catch (error) {
      console.error("Failed to get user role from localStorage:", error);
    }
  };

  // Fetch pipelines and deals

    // Fetch pipelines and deals
  const fetchPipelineAndDeals = async () => {
    let pipelineRes;
    // Determine requester role synchronously from localStorage
    let logged = {};
    try { logged = JSON.parse(localStorage.getItem("userData") || "{}"); } catch (e) { logged = {}; }
    const loggedRole = (logged.role || "").toString().toLowerCase();

    // Only managers can view other users' pipelines
    // Members should always see their own pipeline (no userId param)
    if (userId && loggedRole === "manager") {
      pipelineRes = await getPipelines(userId, dateFilterParams);
      
    } else {
      pipelineRes = await getPipelines(undefined, dateFilterParams);
    }
    console.log("pipelineRes: ", pipelineRes);
    const pipelineObj = {
      _id: pipelineRes.data.pipelineId,
      name: "Pipeline", // or let user select
    };
    console.log("pipelineObj: ", pipelineObj);
    
    

    setPipeline(pipelineObj);
    setPipelineName(pipelineObj.name);
    const dealsRes = pipelineRes;

    // Get custom stage names from localStorage
    const stageNames = JSON.parse(localStorage.getItem("stageNames") || "{}");

    setStages(
      dealsRes.data.stages.map((stage) => ({
        id: String(stage._id || stage.stageId),
        title: stageNames[stage._id] || stage.name, // Use custom name if available, otherwise use API name
        order: stage.order,
        deals: (stage.deals || []).map((deal) => ({
          ...deal,
          id: String(deal._id),
          content: deal.title,
        })),
      }))
    );
  };

  // Re-run when userId or dateFilterParams change (we intentionally call internal helpers here)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log("KANBAN EFFECT", { pathname: location.pathname, userId, dateFilterParams });
    // Only run if not on user detail page
    // if (location.pathname.startsWith("/home/member/")) return;

    fetchPipelineAndDeals();
    fetchLeads();
    fetchTeamMembers();
    getUserRole();
    fetchCompanies(); // (ADDED)
    // Only re-run when userId or the actual date filter values change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dateFilterParams?.startDate, dateFilterParams?.endDate]);

  function findDeal(dealId) {
    for (const stage of stages) {
      const deal = stage.deals.find((d) => d.id === dealId);
      if (deal) return { deal, stageId: stage.id };
    }
    return null;
  }

  function handleDragStart(event) {
    const { active } = event;
    const found = findDeal(active.id);
    if (found) setActiveDeal(found.deal);
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) {
      setActiveDeal(null);
      return;

    }

    //  Prefer structured data from draggable
  const payload = active?.data?.current || {};

  // If this is a lead drag (DraggableLead sets data.type = 'lead')
  if (payload.type === "lead" || String(active.id).startsWith("lead-")) {
    const lead = payload.lead || {
      id: String(active.id).replace(/^lead-/, ""),
    };
    // open form for lead and wait for submit before moving
    setStageFormLead(lead);
    setStageTargetId(over.id);
    setShowStageForm(true);
    setActiveDeal(null); // snap-back visual
    return;
  }

    const from = findDeal(active.id);
    if (!from || from.stageId === over.id) {
      setActiveDeal(null);
      return;
    }

    // //  Show stage transition form
    // setStageFormLead(from.deal);
    // setShowStageForm(true);
    // // Optimistically update UI

    // setStages((prevStages) => {
    //   const newStages = prevStages.map((stage) => {
    //     // Remove from old stage
    //     if (stage.id === from.stageId) {
    //       return {
    //         ...stage,
    //         deals: stage.deals.filter((d) => d.id !== active.id),
    //       };
    //     }
    //     // Add to new stage
    //     if (stage.id === over.id) {
    //       const movedDeal = { ...from.deal };
    //       return {
    //         ...stage,
    //         deals: [...stage.deals, movedDeal],
    //       };
    //     }
    //     return stage;
    //   });
    //   return newStages;
    // });
    // try {
    //   await moveDealToStage(active.id, over.id);
    //   fetchPipelineAndDeals(); // To sync with backend
    // } catch (err) {
    //   // Optionally show error and refetch to revert
    //   fetchPipelineAndDeals();
    // }
    // setActiveDeal(null);

    // Optimistically update UI
    setStages((prevStages) =>
      prevStages.map((stage) => {
        if (stage.id === from.stageId) {
          return { ...stage, deals: stage.deals.filter((d) => d.id !== active.id) };
        }
        if (stage.id === over.id) {
          return { ...stage, deals: [...stage.deals, { ...from.deal }] };
        }
        return stage;
      })
    );
    try {
      await moveDealToStage(active.id, over.id);
      fetchPipelineAndDeals();
    } catch (err) {
      fetchPipelineAndDeals();
    }
    setActiveDeal(null);
  }

  // function normalizeDealPayload(pipelineId, stageId, dealData = {}) {
  //   const payload = {
  //     ...dealData,
  //     pipelineId: pipelineId || dealData.pipelineId,
  //     stageId: stageId || dealData.stageId,
  //   };
  //   console.log("vvvv", payload);
  //   if (payload.companyId && !payload.clientCompanyIds) {
  //     payload.clientCompanyIds = [payload.companyId];
  //   }
  //   if (payload.clientCompanyIds && !Array.isArray(payload.clientCompanyIds)) {
  //     payload.clientCompanyIds = [payload.clientCompanyIds];
  //   }
  //   delete payload.companyId; // (requested)
  //   delete payload.id; // local temp id not needed by backend
  //   delete payload.content; // UI-only alias of title
  //   return payload;
  // }
  function normalizeDealPayload(
    pipelineId,
    stageId,
    dealData = {},
    originalStageId = null
  ) {
    const payload = {
      ...dealData,
    };

    // Only include stageId if it's explicitly changed
    if (stageId && stageId !== originalStageId) {
      payload.stageId = stageId;
    }

    // Same for pipelineId
    if (pipelineId !== undefined && pipelineId !== null) {
      payload.pipelineId = pipelineId;
    }

    if (payload.companyId && !payload.clientCompanyIds) {
      payload.clientCompanyIds = [payload.companyId];
    }
    if (payload.clientCompanyIds && !Array.isArray(payload.clientCompanyIds)) {
      payload.clientCompanyIds = [payload.clientCompanyIds];
    }

    delete payload.companyId;
    delete payload.id;
    delete payload.content;

    return payload;
  }

  async function handleAddDeal(deal) {
    if (!pipeline?._id) {
      console.warn("Pipeline not loaded");
      return;
    }
    if (!deal.stageId) {
      error("Select a stage", { title: "Deal" });
      return;
    }

    // Ensure title present
    if (!deal.title || !deal.title.trim()) {
      error("Deal title is required", { title: "Deal" });
      return;
    }

    // Build payload (adds pipelineId, stageId, maps companyId -> clientCompanyIds)
    const payload = normalizeDealPayload(pipeline._id, deal.stageId, {
      ...deal,
      title: deal.title.trim(),
    });

    try {
      await addDeal(pipeline._id, deal.stageId, payload); // FIX: correct signature
      await fetchPipelineAndDeals();
      success("Deal added", { title: "Deal" });
      setAddDealOpen(false);
    } catch (e) {
      console.error("Add deal failed", e);
      error(e?.response?.data?.message || "Failed to add deal", {
        title: "Deal",
      });
    }
  }

  // async function handleEditDeal(deal) {
  //   if (!deal._id) return;
  //   await editDeals(deal._id, deal);
  //   fetchPipelineAndDeals();
  // }

  async function handleEditDeal(deal) {
    if (!deal?._id && !deal?.id) return;

    // Build payload with clientCompanyIds (and remove companyId, id, content)
    const payload = normalizeDealPayload(pipeline?._id, deal.stageId, {
      ...deal,
      title: deal.title?.trim?.() ?? deal.title,
    });

    try {
      await editDeals(deal._id || deal.id, payload);
      await fetchPipelineAndDeals();
      success("Deal updated", { title: "Deal" });
    } catch (e) {
      console.error("Update deal failed", e);
      error(e?.response?.data?.message || "Update failed", { title: "Deal" });
    } finally {
      setEditDeal(null);
    }
  }

  async function handleAddStage(e) {
    e.preventDefault();
    // Prevent members from adding stages client-side
    if (userRole?.toString().toLowerCase() === "member") {
      error("You are not authorized to add stages", { title: "Unauthorized" });
      return;
    }
    if (!pipeline || !addStageName.trim()) return;
    let payload = {
      name: addStageName.trim(),
      order: stages.length + 1,
    };
    // Add targetType and memberIds/orgId as needed
    if (userRole === "manager" || userRole === "admin") {
      payload.targetType = addStageTargetType;
      if (addStageTargetType === "group") {
        const orgId = localStorage.getItem("orgId");
        // Only include orgId if it exists and is not the string 'null'
        if (orgId && orgId !== 'null') {
          payload.orgId = orgId;
        }
      } else if (addStageTargetType === "members") {
        payload.memberIds = addStageMemberIds;
      } else {
        payload.targetUserId = localStorage.getItem("userId");
      }
    }
    const data = await addStage(pipeline._id, payload);
    console.log("data: ", data);
    if (data.status_code == 201) {
      success(`Stage created successfully!`, {
        title: "Stage",
      });
    }
    setAddStageName("");
    setAddStageMemberIds([]);
    setAddStageTargetType("single");
    fetchPipelineAndDeals();
  }

  function onEditStage(id, title) {
    setEditingStageId(id);
    setEditingStageName(title);
  }
  //  Stage change handler
  function handleStageChange(lead) {
    setStageFormLead(lead);
    setShowStageForm(true);
  }

  function handleStageNameEdit(e) {
    e.preventDefault();

    // Store the new stage name in localStorage
    if (editingStageId && editingStageName.trim()) {
      const stageNames = JSON.parse(localStorage.getItem("stageNames") || "{}");
      stageNames[editingStageId] = editingStageName.trim();
      localStorage.setItem("stageNames", JSON.stringify(stageNames));

      // Update the stages state to reflect the new name
      setStages((prevStages) =>
        prevStages.map((stage) =>
          stage.id === editingStageId
            ? { ...stage, title: editingStageName.trim() }
            : stage
        )
      );
    }

    setEditingStageId(null);
    setEditingStageName("");
  }

  // Lead assignment functions
  const handleAssignLead = (lead) => {
    setLeadToAssign(lead);
    setSelectedTeamMember(null);
    setShowAssignModal(true);
  };

  const handleTeamMemberAssignment = async () => {
    if (!selectedTeamMember || !leadToAssign) {
      error("Please select a team member to assign the lead to.", {
        title: "Selection Required",
      });
      return;
    }

    setAssigningLead(true);
    try {
      await teamAPI.assignLead(leadToAssign.id, selectedTeamMember._id);

      // Extract lead name for success message
      const extractFieldValue = (fieldName) => {
        const field = leadToAssign.field_data?.find(
          (f) => f.name.toLowerCase() === fieldName.toLowerCase()
        );
        return field?.values?.[0] || null;
      };
      const firstName =
        extractFieldValue("first name") || extractFieldValue("first_name");
      const lastName =
        extractFieldValue("last name") || extractFieldValue("last_name");
      const fullName =
        firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || "Lead";

      success(`Lead "${fullName}" assigned successfully!`, {
        title: "Assignment Complete",
      });
      setShowAssignModal(false);
      setLeadToAssign(null);
      setSelectedTeamMember(null);

      // Refresh leads to show updated assignment
      fetchLeads();
    } catch (err) {
      console.error("Failed to assign lead:", err);
      error(
        "Something went wrong while assigning the lead. Please try again.",
        {
          title: "Assignment Failed",
        }
      );
    } finally {
      setAssigningLead(false);
    }
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setLeadToAssign(null);
    setSelectedTeamMember(null);
  };

  // Deal assignment functions
  const handleAssignDeal = (deal) => {
    setDealToAssign(deal);
    setSelectedTeamMember(null);
    setShowDealAssignModal(true);
  };

  const handleDealAssignment = async () => {
    if (!selectedTeamMember || !dealToAssign) {
      error("Please select a team member to assign the deal to.", {
        title: "Selection Required",
      });
      return;
    }

    setAssigningDeal(true);
    try {
      await teamAPI.assignDeal(dealToAssign.id, selectedTeamMember._id);
      success(
        `Deal "${
          dealToAssign.title || dealToAssign.content
        }" assigned successfully!`,
        {
          title: "Assignment Complete",
        }
      );
      setShowDealAssignModal(false);
      setDealToAssign(null);
      setSelectedTeamMember(null);

      // Refresh deals to show updated assignment
      fetchPipelineAndDeals();
    } catch (err) {
      console.error("Failed to assign deal:", err);
      error(
        "Something went wrong while assigning the deal. Please try again.",
        {
          title: "Assignment Failed",
        }
      );
    } finally {
      setAssigningDeal(false);
    }
  };

  const closeDealAssignModal = () => {
    setShowDealAssignModal(false);
    setDealToAssign(null);
    setSelectedTeamMember(null);
  };

  const handleConvertToDeal = (lead) => {
    // Extract proper name from field_data
    const extractFieldValue = (fieldName) => {
      const field = lead.field_data?.find(
        (f) => f.name.toLowerCase() === fieldName.toLowerCase()
      );
      return field?.values?.[0] || null;
    };

    const firstName =
      extractFieldValue("first name") || extractFieldValue("first_name");
    const lastName =
      extractFieldValue("last name") || extractFieldValue("last_name");
    const fullName =
      firstName && lastName
        ? `${firstName} ${lastName}`
        : firstName || "Unknown Lead";
    const email = extractFieldValue("email") || lead.email || "No email";

    // Convert lead to deal by opening the add deal modal with lead data
    setEditDeal({
      title: fullName,
      description: `Converted from Facebook lead: ${email}`,
      value: 0,
      stageId: stages[0]?.id || "",
      code_currency: "INR",
      endDate: "",
      leadId: lead.id, // Store original lead ID for reference
    });
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "0 4px",
          marginBottom: 24,
          flexWrap: "wrap",
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {editingName ? (
            <form
              onSubmit={() => setEditingName(false)}
              style={{ display: "flex", alignItems: "center", gap: 8 }}
            >
              <input
                value={pipelineName}
                onChange={(e) => setPipelineName(e.target.value)}
                autoFocus
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 700,
                  border: "1.5px solid #e3e6f0",
                  borderRadius: 8,
                  padding: "4px 12px",
                  outline: "none",
                  color: "#151d3b",
                  background: "#f4f5fa",
                }}
                maxLength={32}
              />
            </form>
          ) : (
            <h2
              style={{
                fontSize: "28px",
                fontWeight: 600,
                color: "#1a1a1a",
                margin: 0,
              }}
            >
              {pipelineName}
            </h2>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          {typeof setKanbanDateRange === "function" && (
            <CustomFilterBar
              dateRange={kanbanDateRange}
              setDateRange={setKanbanDateRange}
              customStart={kanbanCustomStart}
              setCustomStart={setKanbanCustomStart}
              customEnd={kanbanCustomEnd}
              setCustomEnd={setKanbanCustomEnd}
              DATE_OPTIONS={DATE_OPTIONS}
              style={{ background: "transparent" }}
            />
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => setAddDealOpen(true)}
              style={{
                background: "var(--primary-color)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0 22px",
                fontWeight: 600,
                fontSize: "1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 2px 8px rgba(30,42,90,0.10)",
                transition: "background 0.2s",
                height: 40,
              }}
            >
              <FiPlus style={{ fontSize: 20 }} /> Add Deal
            </button>
          </div>
        </div>
      </div>

      <DealModal
        open={addDealOpen}
        onClose={() => setAddDealOpen(false)}
        onSubmit={handleAddDeal}
        stages={stages}
        companies={companies}
        companiesLoading={loadingCompanies}
        companiesError={companiesError}
        onUpdateCompany={updateCompanyDetails}
        onCreateCompany={createCompany}
        savingCompanyId={savingCompanyId}
        teamMembers={teamMembers}
        userRole={userRole}
        onAssignDeal={handleAssignDeal}
      />
      <DealModal
        open={!!editDeal}
        onClose={() => setEditDeal(null)}
        onSubmit={(deal) => {
          if (deal.leadId && !deal._id) {
            handleAddDeal(deal);
          } else {
            handleEditDeal(deal);
          }
          setEditDeal(null);
        }}
        stages={stages}
        initialData={editDeal}
        isEdit
        companies={companies}
        companiesLoading={loadingCompanies}
        companiesError={companiesError}
        onUpdateCompany={updateCompanyDetails}
        onCreateCompany={createCompany}
        savingCompanyId={savingCompanyId}
        onAssignDeal={handleAssignDeal}
        teamMembers={teamMembers}
        userRole={userRole}
      />
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          style={{
            display: "flex",
            gap: 5,
            overflowX: "auto",
            paddingBottom: 8,
            height: "100vh",
          }}
        >
          {stages.map((stage) => (
            <DroppableStage
              key={stage.id}
              stage={stage}
              onEditStage={onEditStage}
              editingStageId={editingStageId}
              editingStageName={editingStageName}
              setEditingStageName={setEditingStageName}
              setEditingStageId={setEditingStageId}
              handleStageNameEdit={handleStageNameEdit}
              onAssignLead={handleAssignLead}
              onConvertToDeal={handleConvertToDeal}
              leads={leads}
              teamMembers={teamMembers}
              userRole={userRole}
              onOpenLeadDetails={openLeadDetails} // NEW
              // style={{
              //   // border: selectedStageId === stage.id ? "2px solid #1677ff" : "1px solid #eee",
              //   // background: selectedStageId === stage.id ? "#f0f6ff" : "#f4f5fa",
              //   border: selectedStageId === stage.id ? "2px solid red" : "1px solid #eee",
              //   background: selectedStageId === stage.id ? "lightgreen" : "#f4f5fa",
              //   borderRadius: 8,
              //   transition: "all 0.2s",
              // }}
              style={{
                border:
                  String(selectedStageId) === String(stage.id)
                    ? "3px solid red"
                    : "1px solid #eee",
                background:
                  String(selectedStageId) === String(stage.id)
                    ? "lightgreen"
                    : "#f4f5fa",
                borderRadius: 8,
                transition: "all 0.2s",
              }}
            >
              {stage.deals.map((deal) => (
                <DraggableDeal
                  key={deal.id}
                  deal={deal}
                  stageId={stage.id}
                  onEdit={(d, sId) => {
                    setEditDeal({ ...d, stageId: sId });
                    setEditDealStageId(sId);
                  }}
                  onAssign={handleAssignDeal}
                  teamMembers={teamMembers}
                  userRole={userRole}
                  onOpenDetails={(d, sId) => {
                    // OPEN CENTER MODAL IN EDIT MODE
                    // setEditDeal({ ...d, stageId: sId });
                    // setEditDealStageId(sId);
                    openDealDetails(d, sId);
                  }}
                />
              ))}
            </DroppableStage>
          ))}
        </div>
        <DragOverlay>
          {activeDeal ? (
            <div
              style={{
                padding: 16,
                background: "#e0e7ff",
                borderRadius: 10,
                boxShadow: "0 2px 8px rgba(30,42,90,0.15)",
                minWidth: 200,
                fontWeight: 500,
                fontSize: "1.05rem",
                border: "1px solid #e3e6f0",
              }}
            >
              {activeDeal.content}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {detailsOpen && detailsDeal && (
        <div className="modal-overlay" onClick={closeDealDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ borderRadius: 16, overflow: "hidden", padding: 0, maxWidth: 560 }}>
            {/* Header */}
            <div style={{
              padding: "18px 24px",
              borderBottom: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#fff",
            }}>
              <span style={{ fontWeight: 700, fontSize: "1.1rem", color: "#151d3b" }}>Deal Details</span>
              <button onClick={closeDealDetails} style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#6b7280", fontSize: 20, lineHeight: 1,
                width: 32, height: 32, borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>×</button>
            </div>

            <div style={{ padding: "20px 24px", overflowY: "auto", maxHeight: "60vh" }}>
              <div className="lead-details-grid">
                {/* Deal Information */}
                <div className="detail-section">
                  <h4>Deal Information</h4>
                  <div className="detail-item">
                    <strong>Title:</strong>{" "}
                    {detailsDeal.title || detailsDeal.content}
                  </div>
                  {detailsDeal.description && (
                    <div className="detail-item" style={{ display: "flex", gap: 8 }}>
                      <strong style={{ minWidth: 120, flexShrink: 0 }}>Description:</strong>
                      <span>{detailsDeal.description}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <strong>Value:</strong>{" "}
                    {(detailsDeal.code_currency === "INR" && "₹") || (detailsDeal.code_currency === "USD" && "$") || (detailsDeal.code_currency === "EUR" && "€") || ""}
                    {detailsDeal.value ? Number(detailsDeal.value).toLocaleString() : ""}
                  </div>
                  <div className="detail-item">
                    <strong>Status:</strong>{" "}
                    {detailsDeal.status === "closed_won" ? "Closed Won" : detailsDeal.status === "closed_lost" ? "Closed Lost" : detailsDeal.status === "open" ? "Open" : detailsDeal.status || "Unknown"}
                  </div>
                  <div className="detail-item">
                    <strong>Created:</strong>{" "}
                    {detailsDeal.createdAt ? new Date(detailsDeal.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Unknown"}
                  </div>
                </div>

                {/* Company Information */}
                <div className="detail-section">
                  <h4>Company Information</h4>
                  {Array.isArray(detailsDeal.companies) && detailsDeal.companies.length > 0 ? (
                    detailsDeal.companies.map((cc) => (
                      <div key={cc._id} style={{ marginBottom: 12 }}>
                        <div className="detail-item"><strong>Company:</strong> {cc.companyName}</div>
                        <div className="detail-item">
                          <strong>Website:</strong>{" "}
                          {cc.website ? <a href={cc.website} target="_blank" rel="noreferrer">{cc.website}</a> : "Not provided"}
                        </div>
                        <div className="detail-item"><strong>Address:</strong> {cc.address || "Not provided"}</div>
                        <div className="detail-item" style={{ marginTop: 6 }}>
                          <strong>Contacts:</strong>
                          {Array.isArray(cc.contactNumbers) && cc.contactNumbers.length > 0 ? (
                            <div style={{ marginTop: 6 }}>
                              {cc.contactNumbers.map((c) => (
                                <div key={c._id || `${c.name}-${c.number}`} style={{ paddingLeft: 8 }}>
                                  • {c.name || "N/A"} — {c.number || "N/A"}{c.email ? ` — ${c.email}` : ""}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span style={{ marginLeft: 6 }}>No contacts</span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="detail-item">No company attached</div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: "14px 24px",
              borderTop: "1px solid #f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              background: "#fafafa",
            }}>
              <button
                onClick={() => { handleAssignDeal(detailsDeal); closeDealDetails(); }}
                style={{
                  background: "none", border: "1.5px solid #e3e6f0", borderRadius: 8,
                  padding: "7px 16px", cursor: "pointer", fontWeight: 600, fontSize: "0.88rem",
                  color: "#374151", display: "flex", alignItems: "center", gap: 6,
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--primary-color)"; e.currentTarget.style.color = "var(--primary-color)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e3e6f0"; e.currentTarget.style.color = "#374151"; }}
              >
                👤 Assign Deal
              </button>
              <button
                onClick={() => { setEditDeal({ ...detailsDeal, stageId: detailsStageId }); setEditDealStageId(detailsStageId); closeDealDetails(); }}
                style={{
                  background: "var(--primary-color)", border: "none", borderRadius: 8,
                  padding: "7px 20px", cursor: "pointer", fontWeight: 600, fontSize: "0.88rem",
                  color: "#fff", transition: "background 0.2s",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#303f9f"}
                onMouseLeave={(e) => e.currentTarget.style.background = "var(--primary-color)"}
              >
                Edit Deal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* // ...existing code...
       */}

      {leadDetailsOpen && leadDetails && (
        <div className="modal-overlay" onClick={closeLeadDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Lead Details</h3>
              <button className="close-btn" onClick={closeLeadDetails}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="lead-details-grid">
                <div className="detail-section">
                  <h4>Contact Information</h4>
                  {(() => {
                    const extract = (name) =>
                      leadDetails.field_data?.find(
                        (f) => f.name?.toLowerCase() === name.toLowerCase()
                      )?.values?.[0] || null;
                    const firstName =
                      extract("first name") || extract("first_name");
                    const lastName =
                      extract("last name") || extract("last_name");
                    const fullName =
                      firstName && lastName
                        ? `${firstName} ${lastName}`
                        : firstName || leadDetails.name || "Unknown Lead";
                    const email =
                      extract("email") || leadDetails.email || "No email";
                    const phone =
                      extract("phone") ||
                      extract("phone_number") ||
                      leadDetails.phone ||
                      "No phone";
                    const company =
                      extract("company") ||
                      extract("company_name") ||
                      leadDetails.company ||
                      "No company";
                    const jobTitle =
                      extract("job_title") ||
                      extract("job_position") ||
                      leadDetails.job_title ||
                      "No title";

                    return (
                      <>
                        <div className="detail-item">
                          <strong>Name:</strong> {fullName}
                        </div>
                        <div className="detail-item">
                          <strong>Email:</strong> {email}
                        </div>
                        <div className="detail-item">
                          <strong>Phone:</strong> {phone}
                        </div>
                        <div className="detail-item">
                          <strong>Company:</strong> {company}
                        </div>
                        <div className="detail-item">
                          <strong>Job Title:</strong> {jobTitle}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="detail-section">
                  <h4>Location</h4>
                  {(() => {
                                       const extract = (name) =>
                      leadDetails.field_data?.find(
                        (f) => f.name?.toLowerCase() === name.toLowerCase()
                      )?.values?.[0] || null;
                    const city =
                      extract("city") || leadDetails.city || "No city";
                    const state =
                      extract("state") || leadDetails.state || "No state";
                    const country =
                      extract("country") || leadDetails.country || "No country";
                    return (
                      <>
                        <div className="detail-item">
                          <strong>City:</strong> {city}
                        </div>
                        <div className="detail-item">
                          <strong>State:</strong> {state}
                        </div>
                        <div className="detail-item">
                          <strong>Country:</strong> {country}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="detail-section">
                  <h4>Campaign Information</h4>
                  {(() => {
                    const adName =
                      leadDetails.campaign_name ||
                      leadDetails.ad_name ||
                      `Ad ${leadDetails.ad_id || "Unknown"}`;
                    const formName = leadDetails.form_name || "Unknown Form";
                    const adId = leadDetails.ad_id || "Unknown";
                    const campaignId = leadDetails.campaign_id || "Unknown";
                    const formId = leadDetails.form_id || "Unknown";
                    return (
                      <>
                        <div className="detail-item">
                          <strong>Campaign:</strong> {adName}
                        </div>
                        <div className="detail-item">
                          <strong>Form Name:</strong> {formName}
                        </div>
                        <div className="detail-item">
                          <strong>Ad ID:</strong> {adId}
                        </div>
                        <div className="detail-item">
                          <strong>Campaign ID:</strong> {campaignId}
                        </div>
                        <div className="detail-item">
                          <strong>Form ID:</strong> {formId}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="detail-section">
                  <h4>Additional Information</h4>
                  {(() => {
                    const extract = (name) =>
                      leadDetails.field_data?.find(
                        (f) => f.name?.toLowerCase() === name.toLowerCase()
                      )?.values?.[0] || null;
                    const created = leadDetails.created_time
                      ? new Date(leadDetails.created_time).toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )
                      : "Unknown";
                    const statusLbl = leadDetails.status || "new";
                    const website =
                      extract("website") ||
                      leadDetails.website ||
                      "Not provided";
                    const message = extract("message") || leadDetails.message;

                    return (
                      <>
                        <div className="detail-item">
                          <strong>Status:</strong>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: "#1976d2",
                              marginLeft: "8px",
                            }}
                          >
                            {String(statusLbl).toUpperCase()}
                          </span>
                        </div>
                        <div className="detail-item">
                          <strong>Created:</strong> {created}
                        </div>
                        <div className="detail-item">
                          <strong>Website:</strong> {website}
                        </div>
                        {message && message !== "No message" && (
                          <div className="detail-item">
                            <strong>Message:</strong> {message}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {Array.isArray(leadDetails.field_data) &&
                  leadDetails.field_data.length > 0 && (
                    <div className="detail-section">
                      <h4>Form Fields</h4>
                      {leadDetails.field_data.map((field, index) => (
                        <div key={index} className="detail-item">
                          <strong>{field.name}:</strong>{" "}
                          {field.values ? field.values.join(", ") : "No value"}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
            <div className="modal-footer">
              {userRole === "manager" && (
                <button
                  className="assign-btn modal-assign"
                  onClick={() => {
                    handleAssignLead(leadDetails);
                    closeLeadDetails();
                  }}
                >
                  Assign Lead
                </button>
              )}
              {/* Close button intentionally removed as per your earlier request */}
            </div>
          </div>
        </div>
      )}
      {/* // ...existing code...
       */}

      {/* Team Member Assignment Modal */}
      <Dialog
        open={showAssignModal}
        onClose={closeAssignModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: "1rem" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 4,
          }}
        >
          Assign Lead to Team Member
          <IconButton
            onClick={closeAssignModal}
            sx={{ color: "var(--primary-color)" }}
          >
            <FiX />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {leadToAssign && (
            <div
              style={{
                marginBottom: 24,
                padding: 16,
                background: "#f8f9fa",
                borderRadius: 8,
              }}
            >
              <h4 style={{ margin: "0 0 12px 0", color: "#151d3b" }}>
                Lead Information
              </h4>
              {(() => {
                // Extract data from field_data for assignment modal
                const extractFieldValue = (fieldName) => {
                  const field = leadToAssign.field_data?.find(
                    (f) => f.name.toLowerCase() === fieldName.toLowerCase()
                  );
                  return field?.values?.[0] || null;
                };

                const firstName =
                  extractFieldValue("first name") ||
                  extractFieldValue("first_name");
                const lastName =
                  extractFieldValue("last name") ||
                  extractFieldValue("last_name");
                const fullName =
                  firstName && lastName
                    ? `${firstName} ${lastName}`
                    : firstName || "Unknown Lead";
                const email =
                  extractFieldValue("email") ||
                  leadToAssign.email ||
                  "No email";
                const phone =
                  extractFieldValue("phone") ||
                  leadToAssign.phone ||
                  "No phone";

                return (
                  <>
                    <p style={{ margin: "4px 0", color: "#666" }}>
                      <strong>Name:</strong> {fullName}
                    </p>
                    <p style={{ margin: "4px 0", color: "#666" }}>
                      <strong>Email:</strong> {email}
                    </p>
                    {phone !== "No phone" && (
                      <p style={{ margin: "4px 0", color: "#666" }}>
                        <strong>Phone:</strong> {phone}
                      </p>
                    )}
                    <p style={{ margin: "4px 0", color: "#666" }}>
                      <strong>Source:</strong> Facebook Lead
                    </p>
                  </>
                );
              })()}
            </div>
          )}

          <div>
            <h4 style={{ margin: "0 0 16px 0", color: "#151d3b" }}>
              Select Team Member
            </h4>
            {teamMembers.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 24,
                  color: "#666",
                  background: "#f8f9fa",
                  borderRadius: 8,
                }}
              >
                <p>
                  No team members available. Please invite team members first.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {teamMembers.map((member) => (
                  <div
                    key={member.email}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      border:
                        selectedTeamMember &&
                        selectedTeamMember.email === member.email
                          ? "2px solid #1976d2"
                          : "2px solid #e9ecef",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background:
                        selectedTeamMember &&
                        selectedTeamMember.email === member.email
                          ? "#e3f2fd"
                          : "white",
                    }}
                    onClick={() => setSelectedTeamMember(member)}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#151d3b",
                          marginBottom: 2,
                        }}
                      >
                        {member.name || "Unknown"}
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#666",
                          marginBottom: 2,
                        }}
                      >
                        {member.email}
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#1976d2",
                          background: "#e3f2fd",
                          padding: "2px 6px",
                          borderRadius: 4,
                          display: "inline-block",
                        }}
                      >
                        {member.role}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "1.2rem",
                        color: "#1976d2",
                        fontWeight: "bold",
                      }}
                    >
                      {selectedTeamMember &&
                        selectedTeamMember.email === member.email &&
                        "✓"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button onClick={closeAssignModal} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleTeamMemberAssignment}
            disabled={!selectedTeamMember || assigningLead}
            variant="contained"
            sx={{
              background: "var(--primary-color)",
              ":hover": { background: "#303f9f" },
            }}
          >
            {assigningLead ? "Assigning..." : "Assign Lead"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deal Assignment Modal */}
      <Dialog
        open={showDealAssignModal}
        onClose={closeDealAssignModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: { borderRadius: "1rem" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 4,
          }}
        >
          Assign Deal to Team Member
          <IconButton
            onClick={closeDealAssignModal}
            sx={{ color: "var(--primary-color)" }}
          >
            <FiX />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {dealToAssign && (
            <div
              style={{
                marginBottom: 24,
                padding: 16,
                background: "#f8f9fa",
                borderRadius: 8,
              }}
            >
              <h4 style={{ margin: "0 0 12px 0", color: "#151d3b" }}>
                Deal Information
              </h4>
              <p style={{ margin: "4px 0", color: "#666" }}>
                <strong>Title:</strong> {dealToAssign.title}
              </p>
              {dealToAssign.description && (
                <p style={{ margin: "4px 0", color: "#666" }}>
                  <strong>Description:</strong> {dealToAssign.description}
                </p>
              )}
              <p style={{ margin: "4px 0", color: "#666" }}>
                <strong>Value:</strong>{" "}
                {dealToAssign.code_currency === "INR"
                  ? "₹"
                  : dealToAssign.code_currency === "USD"
                  ? "$"
                  : dealToAssign.code_currency === "EUR"
                  ? "€"
                  : ""}
                {dealToAssign.value
                  ? Number(dealToAssign.value).toLocaleString()
                  : ""}
              </p>
            </div>
          )}

          <div>
            <h4 style={{ margin: "0 0 16px 0", color: "#151d3b" }}>
              Select Team Member
            </h4>
            {teamMembers.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 24,
                  color: "#666",
                  background: "#f8f9fa",
                  borderRadius: 8,
                }}
              >
                <p>
                  No team members available. Please invite team members first.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {teamMembers.map((member) => (
                  <div
                    key={member.email}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      border:
                        selectedTeamMember &&
                        selectedTeamMember.email === member.email
                          ? "2px solid #1976d2"
                          : "2px solid #e9ecef",
                      borderRadius: 8,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      background:
                        selectedTeamMember &&
                        selectedTeamMember.email === member.email
                          ? "#e3f2fd"
                          : "white",
                    }}
                    onClick={() => setSelectedTeamMember(member)}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#151d3b",
                          marginBottom: 2,
                        }}
                      >
                        {member.name || "Unknown"}
                      </div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#666",
                          marginBottom: 2,
                        }}
                      >
                        {member.email}
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#1976d2",
                          background: "#e3f2fd",
                          padding: "2px 6px",
                          borderRadius: 4,
                          display: "inline-block",
                        }}
                      >
                        {member.role}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "1.2rem",
                        color: "#1976d2",
                        fontWeight: "bold",
                      }}
                    >
                      {selectedTeamMember &&
                        selectedTeamMember.email === member.email &&
                        "✓"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button
            onClick={closeDealAssignModal}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDealAssignment}
            disabled={!selectedTeamMember || assigningDeal}
            variant="contained"
            sx={{
              background: "var(--primary-color)",
              ":hover": { background: "#303f9f" },
            }}
          >
            {assigningDeal ? "Assigning..." : "Assign Deal"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showStageForm}
        onClose={() => {
          setShowStageForm(false);
          setStageTargetId(null);
          setStageFormLead(null);
        }}
         maxWidth={false}                       // allow custom width from PaperProps
  PaperProps={{ style: { width: "920px", maxWidth: "96vw", borderRadius: "1rem" } }}
      >
        <StageTransitionForm
          leadData={stageFormLead}
          onCancel={() => {
            setShowStageForm(false);
            setStageTargetId(null);
            setStageFormLead(null);
          }}
          onSubmit={async (formData) => {
            try {
              const target = stageTargetId;
              if (!target) throw new Error("Missing target stage");

              // Determine if the opened form was for a Lead (facebook lead) or a Deal
              const source = stageFormLead || {};
              const isLead =
                !!(
                  source.field_data ||
                  source.ad_id ||
                  source.created_time ||
                  source.page_id ||
                  source.message
                ); // heuristics for FB lead

              if (isLead) {
                // For leads: StageTransitionForm already updates lead and deals via API.
                // Just refresh lists and show success.
                await fetchLeads();
                await fetchPipelineAndDeals();
                success("Lead updated", { title: "Stage" });
              } else {
                // For deals: explicitly move deal to target stage
                const dealId = formData._id || formData.id || stageFormLead?.id;
                if (!dealId) throw new Error("Missing deal id");
                await moveDealToStage(dealId, target);
                await fetchPipelineAndDeals();
                success("Deal moved", { title: "Stage" });
              }
            } catch (e) {
              console.error("Move failed", e);
              error(e?.message || "Failed to move", { title: "Stage" });
              // ensure UI consistent
              fetchPipelineAndDeals();
            } finally {
              setShowStageForm(false);
              setStageFormLead(null);
              setStageTargetId(null);
            }
          }}
        />
      </Dialog>

      {/* Toast Component */}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        title={toast.title}
        duration={toast.duration}
        onClose={hideToast}
        position={toast.position}
      />
    </div>
  );
}